import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../api/client';
import { usePreferences } from '../contexts/PreferencesContext';

// ── Type Definitions ──────────────────────────────────────────

interface BoothLocationEntry {
  id: string;
  vendorId: string;
  eventId: string;
  boothNumber: string;
  zone: string | null;
  description: string | null;
  registrationDate: string;
  status: string;
  vendor: {
    id: string;
    businessName: string;
    category: string | null;
  };
  event: {
    id: string;
    name: string;
  };
}

interface EventOption {
  id: string;
  name: string;
}

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const, alignItems: 'center' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const, minWidth: '200px' },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const, minWidth: '180px' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  boothEntry: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0.75rem 0', borderBottom: '1px solid #f0f0f0' },
  boothMain: { flex: 1 },
  boothName: { fontWeight: 600, color: '#1a6b3c', fontSize: '0.95rem' },
  boothNumber: { fontSize: '0.85rem', color: '#555', marginTop: '2px' },
  boothDesc: { fontSize: '0.85rem', color: '#777', marginTop: '4px', fontStyle: 'italic' as const },
  boothZoneBadge: { fontSize: '0.75rem', backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: '12px', fontWeight: 500 },
  emptyState: { textAlign: 'center' as const, color: '#888', padding: '2rem', fontSize: '0.95rem' },
};

// ── Component ─────────────────────────────────────────────────

export function VendorMapPage() {
  const { isDark } = usePreferences();

  const [boothLocations, setBoothLocations] = useState<BoothLocationEntry[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');

  // Fetch booth locations
  const fetchBoothLocations = useCallback(async () => {
    try {
      setError('');
      const params = new URLSearchParams();
      if (selectedEventId) params.set('eventId', selectedEventId);
      if (selectedZone) params.set('zone', selectedZone);
      const query = params.toString() ? `?${params.toString()}` : '';
      const data = await apiClient<BoothLocationEntry[]>(`/vendors/booth-locations${query}`);
      setBoothLocations(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load booth locations');
      setBoothLocations([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, selectedZone]);

  // Fetch events for dropdown
  const fetchEvents = useCallback(async () => {
    try {
      const data = await apiClient<EventOption[]>('/events');
      setEvents(data);
    } catch {
      // Non-critical — dropdown just won't populate
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    setLoading(true);
    fetchBoothLocations();
  }, [fetchBoothLocations]);

  // Derive unique zones from data for the zone dropdown
  const uniqueZones = useMemo(() => {
    const zones = new Set<string>();
    boothLocations.forEach((entry) => {
      if (entry.zone) zones.add(entry.zone);
    });
    return Array.from(zones).sort();
  }, [boothLocations]);

  // Client-side text filter
  const filteredLocations = useMemo(() => {
    if (!searchText.trim()) return boothLocations;
    const term = searchText.toLowerCase();
    return boothLocations.filter(
      (entry) =>
        entry.vendor.businessName.toLowerCase().includes(term) ||
        entry.boothNumber.toLowerCase().includes(term)
    );
  }, [boothLocations, searchText]);

  // Group by zone
  const groupedByZone = useMemo(() => {
    const groups: Record<string, BoothLocationEntry[]> = {};
    filteredLocations.forEach((entry) => {
      const zoneKey = entry.zone || 'Unassigned';
      if (!groups[zoneKey]) groups[zoneKey] = [];
      groups[zoneKey].push(entry);
    });
    // Sort zone keys alphabetically, but put "Unassigned" last
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    return sortedKeys.map((key) => ({ zone: key, entries: groups[key] }));
  }, [filteredLocations]);

  return (
    <div style={styles.page}>
      <h1 style={{ ...styles.title, ...(isDark ? { color: '#c9a84c' } : {}) }}>Vendor Map</h1>

      {error && <div style={styles.error}>{error}</div>}

      {/* Filter Bar */}
      <div style={styles.filterBar}>
        <input
          type="text"
          placeholder="Search by business name or booth #..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={styles.input}
        />
        <select
          value={selectedZone}
          onChange={(e) => setSelectedZone(e.target.value)}
          style={styles.select}
        >
          <option value="">All Zones</option>
          {uniqueZones.map((zone) => (
            <option key={zone} value={zone}>{zone}</option>
          ))}
        </select>
        <select
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
          style={styles.select}
        >
          <option value="">All Events</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>{event.name}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div style={styles.emptyState}>Loading booth locations...</div>
      ) : groupedByZone.length === 0 ? (
        <div style={styles.emptyState}>No booth locations found.</div>
      ) : (
        groupedByZone.map((group) => (
          <div key={group.zone} style={styles.section}>
            <h2 style={styles.sectionTitle}>{group.zone}</h2>
            {group.entries.map((entry) => (
              <div key={entry.id} style={styles.boothEntry}>
                <div style={styles.boothMain}>
                  <div style={styles.boothName}>{entry.vendor.businessName}</div>
                  <div style={styles.boothNumber}>Booth #{entry.boothNumber}</div>
                  <div style={{ fontSize: '0.8rem', color: '#1a6b3c', marginTop: '2px' }}>📅 {entry.event.name}</div>
                  {entry.description && (
                    <div style={styles.boothDesc}>{entry.description}</div>
                  )}
                </div>
                <span style={styles.boothZoneBadge}>{entry.zone || 'Unassigned'}</span>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
